import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, KeyRound, Calendar, Power, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

const fetchAuth = async (path, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('adminToken');
    toast.error('Session expired — please log in again');
    setTimeout(() => { window.location.href = '/admin'; }, 1200);
  }
  return res;
};

export default function PressCodesManager() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ recipient: '', code: '', expires: '', notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchAuth('/api/admin/press-codes');
      const data = await res.json();
      setCodes(data.codes || []);
    } catch (e) {
      toast.error('Could not load press codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCode = async (e) => {
    e.preventDefault();
    if (!form.recipient.trim()) {
      toast.error('Recipient is required');
      return;
    }
    const res = await fetchAuth('/api/admin/press-codes', {
      method: 'POST',
      body: JSON.stringify(form),
    });
    if (res.status === 401) return;
    const data = await res.json();
    if (!res.ok) { toast.error(data.detail || 'Failed to create code'); return; }
    toast.success(`Code ${data.code.code} created for ${data.code.recipient}`);
    setForm({ recipient: '', code: '', expires: '', notes: '' });
    setShowForm(false);
    load();
  };

  const toggleActive = async (c) => {
    const res = await fetchAuth(`/api/admin/press-codes/${c.id}`, {
      method: 'PUT',
      body: JSON.stringify({ active: !c.active }),
    });
    if (res.ok) {
      toast.success(c.active ? 'Code revoked' : 'Code re-enabled');
      load();
    }
  };

  const deleteCode = async (c) => {
    if (!window.confirm(`Permanently delete the code for ${c.recipient}?`)) return;
    const res = await fetchAuth(`/api/admin/press-codes/${c.id}`, { method: 'DELETE' });
    if (res.ok) {
      toast.success('Code deleted');
      load();
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied: ${text}`);
  };

  const isExpired = (expires) => {
    if (!expires) return false;
    return expires < new Date().toISOString().slice(0, 10);
  };

  return (
    <div data-testid="press-codes-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Press Access Codes</h2>
          <p className="text-white/60 text-sm mt-1">Manage who can log into the Media Portal at /media</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="new-press-code-btn"
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#c4a030] text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> New Access Code
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={createCode}
          data-testid="press-code-form"
          className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6 grid md:grid-cols-2 gap-4"
        >
          <div className="flex items-center justify-between md:col-span-2">
            <h3 className="font-bold text-white">Create new code</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-white/50 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Recipient *</label>
            <input
              value={form.recipient}
              onChange={(e) => setForm({ ...form, recipient: e.target.value })}
              placeholder="e.g. The Guardian"
              data-testid="press-code-recipient"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Custom code (leave blank to auto-generate)</label>
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="Auto-generated"
              data-testid="press-code-value"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Expires (optional)</label>
            <input
              type="date"
              value={form.expires}
              onChange={(e) => setForm({ ...form, expires: e.target.value })}
              data-testid="press-code-expires"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Notes (internal only)</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Reviewing Ghost House"
              data-testid="press-code-notes"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-white/70 hover:text-white">Cancel</button>
            <button
              type="submit"
              data-testid="press-code-save"
              className="bg-[#D4AF37] hover:bg-[#c4a030] text-white px-4 py-2 rounded-lg font-medium"
            >
              Create Code
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-white/60">Loading...</p>
      ) : codes.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-10 text-center">
          <KeyRound className="w-10 h-10 mx-auto text-white/30 mb-3" />
          <p className="text-white/60">No press access codes yet. Click "New Access Code" to create one.</p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-left text-sm" data-testid="press-codes-table">
            <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Recipient</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const expired = isExpired(c.expires);
                const status = !c.active ? 'Revoked' : expired ? 'Expired' : 'Active';
                const statusColor = !c.active ? 'bg-gray-500/20 text-gray-300' : expired ? 'bg-orange-500/20 text-orange-300' : 'bg-green-500/20 text-green-300';
                return (
                  <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`press-code-row-${c.code}`}>
                    <td className="px-4 py-3 text-white">
                      {c.recipient}
                      {c.notes && <div className="text-white/40 text-xs mt-1">{c.notes}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => copy(c.code)}
                        className="font-mono bg-white/10 hover:bg-white/20 text-[#D4AF37] px-2 py-1 rounded inline-flex items-center gap-2"
                        title="Click to copy"
                        data-testid={`copy-code-${c.code}`}
                      >
                        {c.code} <Copy className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {c.expires ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {c.expires}
                        </span>
                      ) : (
                        <span className="text-white/40">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70">{c.use_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${statusColor}`}>{status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => toggleActive(c)}
                          title={c.active ? 'Revoke' : 'Re-enable'}
                          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded"
                          data-testid={`toggle-code-${c.code}`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteCode(c)}
                          title="Delete permanently"
                          className="p-2 text-white/60 hover:text-red-400 hover:bg-white/10 rounded"
                          data-testid={`delete-code-${c.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
